import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import LogoutButton from "./components/LogoutButton.jsx";
import { fetchPlanningPrConfig, savePlanningPrConfig } from "./apiPlanningPr.js";
import { confirmDialog } from "./ui/dialogService.js";

const MOIS_FR = [
  "Janvier",
  "Fevrier",
  "Mars",
  "Avril",
  "Mai",
  "Juin",
  "Juillet",
  "Aout",
  "Septembre",
  "Octobre",
  "Novembre",
  "Decembre",
];

function pad2(n) {
  return String(n).padStart(2, "0");
}

function makeMoisCle(annee, moisIndex) {
  const y = Number(annee);
  const mi = Number(moisIndex);
  if (!Number.isFinite(y) || !Number.isFinite(mi)) return "";
  return `${y}-${pad2(mi + 1)}`;
}

function makeLabel(annee, moisIndex) {
  const y = Number(annee);
  const mi = Number(moisIndex);
  if (!Number.isFinite(y) || !Number.isFinite(mi) || mi < 0 || mi > 11) return "";
  return `${MOIS_FR[mi]} ${y}`;
}

function lastDayOfMonthIso(annee, moisIndex) {
  const y = Number(annee);
  const mi = Number(moisIndex);
  if (!Number.isFinite(y) || !Number.isFinite(mi)) return "";
  const d = new Date(y, mi + 1, 0);
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
}

function isoToFr(iso) {
  if (!iso || typeof iso !== "string") return "";
  const m = iso.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return "";
  return `${m[3]}/${m[2]}/${m[1]}`;
}

export default function ConfigDesiderataPlanningPR() {
  const navigate = useNavigate();
  const location = useLocation();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [msg, setMsg] = useState(null);

  const [moisOuverts, setMoisOuverts] = useState([]);
  const [selectedIdx, setSelectedIdx] = useState(0);

  const current = useMemo(() => moisOuverts[selectedIdx] || null, [moisOuverts, selectedIdx]);

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        setError(null);
        setMsg(null);

        const json = (await fetchPlanningPrConfig()) || {};
        const arr = Array.isArray(json.mois_ouverts) ? json.mois_ouverts : [];
        setMoisOuverts(arr);
        setSelectedIdx(0);
      } catch (e) {
        console.error(e);
        setError(e.message || "Erreur inconnue");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(location.search || "");
    const moisFromUrl = String(params.get("mois") || "").trim();
    if (!moisFromUrl || !Array.isArray(moisOuverts) || moisOuverts.length === 0) return;
    const idx = moisOuverts.findIndex((m) => String(m?.mois_cle || "") === moisFromUrl);
    if (idx >= 0 && idx !== selectedIdx) setSelectedIdx(idx);
  }, [location.search, moisOuverts, selectedIdx]);

  const styles = useMemo(
    () => ({
      container: {
        maxWidth: 980,
        margin: "40px auto",
        padding: 24,
        fontFamily: "Inter, sans-serif",
      },
      title: { fontSize: 28, fontWeight: 800, margin: "16px 0 8px" },
      subtitle: { color: "#374151", marginBottom: 16, lineHeight: 1.45 },
      btn: (disabled = false, variant = "dark") => ({
        padding: "10px 18px",
        borderRadius: 10,
        border: "1px solid #111827",
        background:
          variant === "dark"
            ? disabled
              ? "#6b7280"
              : "#111827"
            : "#ffffff",
        color: variant === "dark" ? "#ffffff" : "#111827",
        cursor: disabled ? "not-allowed" : "pointer",
        fontWeight: 700,
        opacity: disabled ? 0.85 : 1,
      }),
      card: {
        border: "1px solid #e5e7eb",
        borderRadius: 12,
        padding: 16,
        background: "#fff",
      },
      grid: {
        display: "grid",
        gridTemplateColumns: "1fr 1fr",
        gap: 12,
      },
      field: { display: "flex", flexDirection: "column", gap: 6 },
      label: { fontSize: 13, fontWeight: 700, color: "#111827" },
      input: {
        padding: "10px 12px",
        borderRadius: 10,
        border: "1px solid #d1d5db",
        fontSize: 14,
      },
      hint: { fontSize: 12, color: "#6b7280", marginTop: 6 },
      row: { display: "flex", gap: 12, flexWrap: "wrap", marginTop: 16, alignItems: "center" },
      ok: { color: "#065f46", fontWeight: 800, marginTop: 12 },
      err: { color: "#b91c1c", fontWeight: 800, marginTop: 12 },
      warn: { color: "#92400e", fontWeight: 800, marginTop: 12 },
      select: {
        padding: "10px 12px",
        borderRadius: 10,
        border: "1px solid #d1d5db",
        fontSize: 14,
        minWidth: 320,
      },
      small: { fontSize: 12, color: "#6b7280" },
      badge: (primary) => ({
        display: "inline-flex",
        alignItems: "center",
        padding: "4px 10px",
        borderRadius: 9999,
        border: "1px solid #d1d5db",
        background: primary ? "#eef2ff" : "#f9fafb",
        color: primary ? "#1d4ed8" : "#374151",
        fontSize: 12,
        fontWeight: 800,
      }),
    }),
    []
  );

  const setField = (k, v) => {
    setMsg(null);
    setError(null);
    setMoisOuverts((prev) => {
      const next = [...prev];
      if (!next[selectedIdx]) return prev;
      next[selectedIdx] = { ...next[selectedIdx], [k]: v };
      return next;
    });
  };

  const autoMoisCle = () => {
    if (!current) return;
    setField("mois_cle", makeMoisCle(current.annee, current.mois_index));
  };

  const autoLabel = () => {
    if (!current) return;
    setField("mois_label", makeLabel(current.annee, current.mois_index));
  };

  const addNextMonth = () => {
    if (!current) return;
    const y = Number(current.annee);
    const mi = Number(current.mois_index);
    if (!Number.isFinite(y) || !Number.isFinite(mi)) return;

    let ny = y;
    let nmi = mi + 1;
    if (nmi > 11) {
      nmi = 0;
      ny = y + 1;
    }

    const entry = {
      annee: ny,
      mois_index: nmi,
      mois_label: makeLabel(ny, nmi),
      mois_cle: makeMoisCle(ny, nmi),
      deadline_iso: lastDayOfMonthIso(ny, nmi),
      deadline_affichage: isoToFr(lastDayOfMonthIso(ny, nmi)),
    };

    setMoisOuverts((prev) => [...prev, entry]);
    setSelectedIdx(moisOuverts.length);
    setMsg(null);
    setError(null);
  };

  const setPrimary = () => {
    if (!current) return;
    setMoisOuverts((prev) => {
      const next = [...prev];
      const picked = next.splice(selectedIdx, 1)[0];
      next.unshift(picked);
      return next;
    });
    setSelectedIdx(0);
    setMsg(null);
    setError(null);
  };

  const removeCurrent = async () => {
    if (!current) return;
    const confirmMsg =
      moisOuverts.length <= 1
        ? "Attention: c'est le dernier mois ouvert. Si vous le supprimez, les desideratas planning PR seront fermes. Continuer ?"
        : "Supprimer ce mois ouvert ?";
    if (!(await confirmDialog(confirmMsg))) return;

    setMoisOuverts((prev) => {
      const next = [...prev];
      next.splice(selectedIdx, 1);
      return next;
    });
    setSelectedIdx((i) => Math.max(0, i - 1));
    setMsg(null);
    setError(null);
  };

  const closeAllDesiderata = async () => {
    if (!(await confirmDialog("Fermer TOUS les desideratas planning PR ?"))) return;
    try {
      setSaving(true);
      setMsg(null);
      setError(null);
      await savePlanningPrConfig({ mois_ouverts: [], restrictions_par_mois: {} });
      setMoisOuverts([]);
      setSelectedIdx(0);
      setMsg("Desideratas planning PR fermes (aucun mois ouvert).");
    } catch (e) {
      console.error(e);
      setError(e.message || "Erreur fermeture desideratas planning PR");
    } finally {
      setSaving(false);
    }
  };

  const save = async () => {
    try {
      setSaving(true);
      setMsg(null);
      setError(null);

      // Regle metier voulue: pas de restriction nominative sur cet ecran.
      // L'eligibilite reste assuree par le backend: seniors PR1 interieurs uniquement.
      const restrictionsParMois = {};
      moisOuverts.forEach((m) => {
        const key = String(m?.mois_cle || "").trim();
        if (key) {
          restrictionsParMois[key] = {
            acces_restreint: false,
            medecins_autorises: [],
          };
        }
      });

      const payload = {
        mois_ouverts: moisOuverts.map((m) => ({
          ...m,
          annee: Number(m.annee),
          mois_index: Number(m.mois_index),
        })),
        restrictions_par_mois: restrictionsParMois,
      };

      await savePlanningPrConfig(payload);

      if (moisOuverts.length === 0) {
        setMsg("Desideratas planning PR fermes (aucun mois ouvert).");
      } else {
        setMsg(`Configuration planning PR enregistree (${moisOuverts.length} mois ouvert${moisOuverts.length > 1 ? "s" : ""}).`);
      }
    } catch (e) {
      console.error(e);
      setError(e.message || "Erreur inconnue");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div style={styles.container}>
        <button style={styles.btn(false)} onClick={() => navigate("/responsable/planning-pr")}>
          ← Retour
        </button>
        <p style={{ marginTop: 14 }}>Chargement de la configuration planning PR...</p>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "16px", flexWrap: "wrap", gap: "12px" }}>
        <button style={styles.btn(false)} onClick={() => navigate("/responsable/planning-pr")}>
          ← Retour a la gestion planning PR
        </button>
        <LogoutButton />
      </div>

      <h1 style={styles.title}>Configuration des desiderata planning Port Royal</h1>
      <p style={styles.subtitle}>
        Ouvrir un ou plusieurs mois pour la saisie des desiderata planning Port Royal.
        <br />
        <span style={styles.small}>
          La gestion d'eligibilite medecins est faite sur l'ecran "Gerer les acces medecins". Seuls les seniors PR1 interieurs sont eligibles.
        </span>
      </p>

      <div style={styles.card}>
        {moisOuverts.length === 0 ? (
          <div style={{ padding: "20px 0" }}>
            <div style={{ ...styles.warn, marginTop: 0, marginBottom: 16 }}>
              Les desideratas planning PR sont actuellement fermes (aucun mois ouvert).
            </div>
            <button
              type="button"
              style={styles.btn(false)}
              onClick={() => {
                const now = new Date();
                const next = new Date(now.getFullYear(), now.getMonth() + 1, 1);
                const y = next.getFullYear();
                const mi = next.getMonth();
                const entry = {
                  annee: y,
                  mois_index: mi,
                  mois_label: makeLabel(y, mi),
                  mois_cle: makeMoisCle(y, mi),
                  deadline_iso: lastDayOfMonthIso(y, mi),
                  deadline_affichage: isoToFr(lastDayOfMonthIso(y, mi)),
                };
                setMoisOuverts([entry]);
                setSelectedIdx(0);
              }}
            >
              + Ouvrir les desideratas (mois prochain)
            </button>
            {msg ? <div style={styles.ok}>{msg}</div> : null}
            {error ? <div style={styles.err}>{error}</div> : null}
          </div>
        ) : (
          <>
            <div style={styles.row}>
              <select
                style={styles.select}
                value={selectedIdx}
                onChange={(e) => setSelectedIdx(Number(e.target.value))}
              >
                {moisOuverts.map((m, idx) => (
                  <option key={String(m.mois_cle || idx)} value={idx}>
                    {m.mois_label || m.mois_cle || `Mois #${idx + 1}`} {idx === 0 ? "— (principal)" : ""}
                  </option>
                ))}
              </select>

              <span style={styles.badge(selectedIdx === 0)}>Mois {selectedIdx === 0 ? "principal" : "ouvert"}</span>

              <button type="button" style={styles.btn(false, "light")} onClick={addNextMonth}>
                + Ajouter le mois suivant
              </button>

              <button type="button" style={styles.btn(false, "light")} onClick={setPrimary}>
                Definir comme principal
              </button>

              <button type="button" style={styles.btn(false, "light")} onClick={removeCurrent}>
                Supprimer ce mois
              </button>

              <button
                type="button"
                style={{ ...styles.btn(false, "light"), color: "#b91c1c", borderColor: "#b91c1c" }}
                onClick={closeAllDesiderata}
              >
                Fermer tous les desideratas
              </button>
            </div>

            {!current ? (
              <div style={styles.err}>Aucun mois selectionne.</div>
            ) : (
              <>
                <div style={{ ...styles.grid, marginTop: 12 }}>
                  <div style={styles.field}>
                    <div style={styles.label}>Annee</div>
                    <input
                      style={styles.input}
                      value={current.annee ?? ""}
                      onChange={(e) => setField("annee", e.target.value)}
                      placeholder="2026"
                    />
                  </div>

                  <div style={styles.field}>
                    <div style={styles.label}>mois_index (0=janvier ... 11=decembre)</div>
                    <input
                      style={styles.input}
                      value={current.mois_index ?? ""}
                      onChange={(e) => setField("mois_index", e.target.value)}
                      placeholder="4"
                    />
                  </div>

                  <div style={styles.field}>
                    <div style={styles.label}>Libelle mois (affichage)</div>
                    <input
                      style={styles.input}
                      value={current.mois_label ?? ""}
                      onChange={(e) => setField("mois_label", e.target.value)}
                      placeholder="Mai 2026"
                    />
                  </div>

                  <div style={styles.field}>
                    <div style={styles.label}>Cle mois (YYYY-MM)</div>
                    <input
                      style={styles.input}
                      value={current.mois_cle ?? ""}
                      onChange={(e) => setField("mois_cle", e.target.value)}
                      placeholder="2026-05"
                    />
                  </div>

                  <div style={styles.field}>
                    <div style={styles.label}>Date limite (ISO, YYYY-MM-DD)</div>
                    <input
                      style={styles.input}
                      value={current.deadline_iso ?? ""}
                      onChange={(e) => setField("deadline_iso", e.target.value)}
                      placeholder="2026-05-31"
                    />
                  </div>

                  <div style={styles.field}>
                    <div style={styles.label}>Date limite (affichage, JJ/MM/AAAA)</div>
                    <input
                      style={styles.input}
                      value={current.deadline_affichage ?? ""}
                      onChange={(e) => setField("deadline_affichage", e.target.value)}
                      placeholder="31/05/2026"
                    />
                  </div>
                </div>

                <div style={styles.row}>
                  <button type="button" style={styles.btn(false, "light")} onClick={autoMoisCle}>
                    Auto mois cle
                  </button>

                  <button type="button" style={styles.btn(false, "light")} onClick={autoLabel}>
                    Auto libelle
                  </button>

                  <button type="button" style={styles.btn(saving)} onClick={save} disabled={saving}>
                    {saving ? "Enregistrement..." : "Enregistrer"}
                  </button>
                </div>

                <div style={styles.hint}>
                  Pour ouvrir plusieurs mois en parallele: selectionnez un mois puis cliquez "Ajouter le mois suivant", ajustez au besoin, puis "Enregistrer".
                </div>

                {msg ? <div style={styles.ok}>{msg}</div> : null}
                {error ? <div style={styles.err}>{error}</div> : null}
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
}
